import { WSButton, WSList, WSText } from "@wingspanhq/fe-component-library";
import { wsMoment } from "@wingspanhq/utils/dist/date/wsMoment";
import React from "react";
import { useUserId } from "../../query/hooks/helpers";
import { useQueryMemberProfile } from "../../query/users/queries";
import { selectorHasPayerContextUser } from "../../shared/selectors/selectorHasPayerContextUser";
import { selectorIsEnterpriseUser } from "../../shared/selectors/selectorIsEnterpriseUser";
import {
  BankHoliday,
  getUpcomingBankHolidays
} from "../../shared/utils/bankHolidays";
import { WS_LINKS } from "../../types/wsLinks";
import { StaticBanner } from "../StaticBanner/StaticBanner";

const PAYROLL_NOTE =
  "Any payroll scheduled on a holiday runs on the preceding business day, and its cutoff date moves earlier accordingly.";

const formatHolidayLabel = (holiday: BankHoliday): string =>
  `${holiday.name}, ${wsMoment(holiday.date, "YYYY-MM-DD").format(
    "dddd, MMMM D"
  )}`;

/**
 * Page-level warning shown in every payer account when one or more bank
 * holidays fall within the next 7 business days, so payers can plan payment
 * timing. Self-gating: renders null for payee-only accounts or when no holiday
 * is upcoming. Mounted in the `BaseAppLayout` header (WIN-15021).
 */
export const BannerBankHoliday: React.FC = () => {
  const userId = useUserId();
  const queryMember = useQueryMemberProfile(userId);

  const isPayerView =
    !!queryMember.data &&
    (selectorIsEnterpriseUser(queryMember.data.user) ||
      selectorHasPayerContextUser(queryMember.data.user));

  const holidays = isPayerView ? getUpcomingBankHolidays() : [];

  const learnMoreLink = (
    <WSButton.Link
      onClick={() =>
        window.open(
          WS_LINKS.payablesPayrollSchedule,
          "_blank",
          "noopener,noreferrer"
        )
      }
    >
      Learn more about your payroll schedule
    </WSButton.Link>
  );

  return (
    <StaticBanner
      kind="Warning"
      name="bankHoliday"
      isVisible={holidays.length > 0}
      style={{ textAlign: "center" }}
    >
      {holidays.length > 1 ? (
        <WSList gap="XS">
          {holidays.map(holiday => (
            <WSText.ParagraphSm key={holiday.date} weight="medium">
              {formatHolidayLabel(holiday)}
            </WSText.ParagraphSm>
          ))}
          <WSText.ParagraphSm>
            Payments will not be processed on these days. {PAYROLL_NOTE}{" "}
            {learnMoreLink}
          </WSText.ParagraphSm>
        </WSList>
      ) : (
        <WSText.ParagraphSm>
          <b>{holidays[0] && formatHolidayLabel(holidays[0])}</b> — payments
          will not be processed that day. {PAYROLL_NOTE} {learnMoreLink}
        </WSText.ParagraphSm>
      )}
    </StaticBanner>
  );
};
